import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import {
  Flex,
  Heading,
  Text,
  TextField,
  TextArea,
  Button,
  Icon,
  IconButton,
  DropdownMenu,
  RadioGroup,
  Badge,
  Checkbox,
  DataTable,
} from '@scale/scaleui-radix';
import { faPlus, faTrash, faPenField } from '@fortawesome/pro-regular-svg-icons';
import { AuditRubricCriterion } from '@scale/shared-types';
import { CriterionQuestionText } from './CriterionQuestionText';

export const FillCriterionAnswerOptions = ({
  criterion,
  selectedOption,
  setSelectedOption,
}: {
  criterion: AuditRubricCriterion;
  selectedOption?: string;
  setSelectedOption: (option?: string) => void;
}) => {
  return (
    <Flex direction="column" gap="2">
      <CriterionQuestionText criterion={criterion} />
      <RadioGroup.Root value={selectedOption ?? ''} onValueChange={setSelectedOption}>
        {criterion.answerOptions.map((option, index) => (
          <RadioGroup.Item
            value={option.text}
            key={index}
            onClick={() => {
              if (selectedOption === option.text) {
                setSelectedOption(undefined);
              }
            }}
          >
            <Flex direction="row" gap="2" align="center">
              <Text>{option.text}</Text>
              {option.requiresJustification && (
                <Text size="1" color="neutral" className="italic">
                  (requires feedback)
                </Text>
              )}
            </Flex>
          </RadioGroup.Item>
        ))}
      </RadioGroup.Root>
    </Flex>
  );
};

const ViewCriterionAnswerOptions = ({ criterion }: { criterion: AuditRubricCriterion }) => {
  return (
    <Flex direction="column" gap="2">
      <Text color="neutral">Rating Options</Text>
      <Flex direction="row" gap="2" wrap="wrap">
        {criterion.answerOptions.map((option, index) => (
          <Badge key={index} color="neutral" className="max-w-full">
            <Text size="1" className="whitespace-normal break-words">
              {option.text} ({option.score})
            </Text>
            {option.requiresJustification && (
              <Icon icon={faPenField} label="justification" size="1" />
            )}
          </Badge>
        ))}
      </Flex>
    </Flex>
  );
};

type AnswerOptionBase = {
  text: string;
  score: number;
  requiresJustification: boolean;
};

type AnswerOptionRow = AnswerOptionBase & {
  index: number;
};

const getColumns = (
  hasError: boolean,
  setOptionByIndex: (index: number) => (option: AnswerOptionRow) => void,
  removeOptionByIndex: (index: number) => () => void,
) => [
  {
    id: 'text',
    header: function HeaderCell() {
      return <DataTable.CellContentDefault>Text</DataTable.CellContentDefault>;
    },
    accessorFn: (row: AnswerOptionRow) => row.text,
    cell: function TextFieldCell(info: { row: { original: AnswerOptionRow } }) {
      const option = info.row.original;
      const setOption = setOptionByIndex(option.index);
      return (
        <div className="flex items-center h-full w-full">
          <TextArea
            style={{
              ...(hasError ? { boxShadow: 'inset 0 0 0 1px #dc2626' } : {}),
              minHeight: 'var(--space-6)', // Single-line height to match TextField size 2
            }}
            size="2"
            placeholder="Answer choice"
            value={option.text}
            onChange={e => setOption({ ...option, text: e.target.value })}
            color={hasError ? 'red' : undefined}
            className="w-full"
            rows={1}
            autoResize
          />
        </div>
      );
    },
  },
  {
    id: 'score',
    header: function HeaderCell() {
      return <DataTable.CellContentDefault>Score</DataTable.CellContentDefault>;
    },
    accessorFn: (row: AnswerOptionRow) => row.score,
    cell: function ScoreCell(info: { row: { original: AnswerOptionRow } }) {
      const option = info.row.original;
      const setOption = setOptionByIndex(option.index);
      return (
        <Flex align="center" className="flex items-center justify-center h-full w-full">
          <TextField.Root
            style={{ ...(hasError ? { boxShadow: 'inset 0 0 0 1px #dc2626' } : {}) }}
            size="2"
            type="number"
            placeholder="Score 1-5"
            value={option.score}
            onChange={e => setOption({ ...option, score: Number(e.target.value) || 0 })}
            color={hasError ? 'red' : undefined}
          />
        </Flex>
      );
    },
    size: 50,
  },
  {
    id: 'requiresJustification',
    header: function HeaderCell() {
      return <DataTable.CellContentDefault>Justification</DataTable.CellContentDefault>;
    },
    accessorFn: (row: AnswerOptionRow) => row.requiresJustification,
    cell: function JustificationCell(info: { row: { original: AnswerOptionRow } }) {
      const option = info.row.original;
      const setOption = setOptionByIndex(option.index);
      return (
        <Flex gap="1" align="center" className="flex items-center justify-center h-full w-full">
          <Checkbox
            size="1"
            checked={option.requiresJustification}
            onCheckedChange={() =>
              setOption({
                ...option,
                requiresJustification: !option.requiresJustification,
              })
            }
          />
          <Text size="1" color="neutral">
            Req.
          </Text>
        </Flex>
      );
    },
    size: 50,
  },
  {
    id: 'action',
    header: '',
    cell: function ActionCell(info: { row: { original: AnswerOptionRow } }) {
      const option = info.row.original;
      const removeOption = removeOptionByIndex(option.index);
      return (
        <Flex align="center" className="flex items-center justify-center h-full w-full">
          <IconButton variant="ghost" color="neutral" onClick={removeOption} size="1">
            <Icon label="delete" color="neutral" icon={faTrash} size="2" />
          </IconButton>
        </Flex>
      );
    },
    size: 24,
  },
];

const EditCriterionAnswerOptions = ({
  criterion,
  setCriterion,
  error,
  questionError,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: Dispatch<SetStateAction<AuditRubricCriterion>>;
  error?: string;
  questionError?: string;
}) => {
  const [showQuestionDescription, setShowQuestionDescription] = useState<boolean>(
    !!criterion.questionDescription,
  );

  const setOptionByIndex = useCallback(
    (index: number) => (option: AnswerOptionBase | AnswerOptionRow) => {
      const nextOption: AnswerOptionBase = {
        text: option.text,
        score: option.score,
        requiresJustification: option.requiresJustification,
      };

      setCriterion(prev => ({
        ...prev,
        answerOptions: prev.answerOptions.map((o, i) => (i === index ? nextOption : o)),
      }));
    },
    [setCriterion],
  );

  const addOption = useCallback(() => {
    setCriterion(prev => ({
      ...prev,
      answerOptions: [...prev.answerOptions, { text: '', score: 3, requiresJustification: false }],
    }));
  }, [setCriterion]);

  const removeOptionByIndex = useCallback(
    (index: number) => () => {
      setCriterion(prev => ({
        ...prev,
        answerOptions: prev.answerOptions.filter((_, i) => i !== index),
      }));
    },
    [setCriterion],
  );

  const loadDisagreeAgreeTemplate = useCallback(() => {
    setCriterion(prev => ({
      ...prev,
      answerOptions: [
        { text: 'Strongly Disagree', score: 1, requiresJustification: false },
        { text: 'Disagree', score: 2, requiresJustification: false },
        { text: 'Neutral', score: 3, requiresJustification: false },
        { text: 'Agree', score: 4, requiresJustification: false },
        { text: 'Strongly Agree', score: 5, requiresJustification: false },
      ],
    }));
  }, [setCriterion]);
  const loadYesNoTemplate = useCallback(() => {
    setCriterion(prev => ({
      ...prev,
      answerOptions: [
        { text: 'No', score: 1, requiresJustification: false },
        { text: 'Yes', score: 5, requiresJustification: false },
      ],
    }));
  }, [setCriterion]);

  const handleShowQuestionDescriptionChange = () => {
    const checked = !showQuestionDescription;
    setShowQuestionDescription(checked);
    if (!checked) {
      setCriterion(prev => ({ ...prev, questionDescription: '' }));
    }
  };

  const columns = useMemo(
    () =>
      getColumns(
        !!error,
        setOptionByIndex as (index: number) => (option: AnswerOptionRow) => void,
        removeOptionByIndex,
      ),
    [error, removeOptionByIndex, setOptionByIndex],
  );

  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" justify="between" align="center">
        <Heading size="2">Rating question</Heading>
        <Flex direction="row" gap="2" align="center">
          <Checkbox
            size="1"
            checked={showQuestionDescription}
            onCheckedChange={handleShowQuestionDescriptionChange}
          />
          <Text size="1" color="neutral">
            Add a description
          </Text>
        </Flex>
      </Flex>
      <TextField.Root
        placeholder="Rating question"
        value={criterion.questionText}
        onChange={e => setCriterion(prev => ({ ...prev, questionText: e.target.value }))}
        className="w-full"
        color={questionError ? 'red' : undefined}
        style={questionError ? { boxShadow: 'inset 0 0 0 1px #dc2626' } : undefined}
      />
      {showQuestionDescription && (
        <>
          <Text size="1" color="neutral">
            Description
          </Text>
          <TextArea
            placeholder="Rating question description"
            value={criterion.questionDescription}
            onChange={e => setCriterion(prev => ({ ...prev, questionDescription: e.target.value }))}
            className="w-full"
            color={questionError ? 'red' : undefined}
            style={questionError ? { boxShadow: 'inset 0 0 0 1px #dc2626' } : undefined}
          />
        </>
      )}
      {questionError && (
        <Text size="2" color="red">
          {questionError}
        </Text>
      )}
      <Flex direction="row" gap="2" justify="between" align="center">
        <Heading size="2">Rating options</Heading>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <Button variant="outline" size="1" color="neutral">
              Load Template
              <DropdownMenu.TriggerIcon />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content size="2">
            <DropdownMenu.Item onClick={loadDisagreeAgreeTemplate}>
              <Flex direction="column" gap="1">
                <Text weight="medium" size="1">
                  Disagree-Agree
                </Text>
                <Text size="1" color="neutral">
                  5 options
                </Text>
              </Flex>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={loadYesNoTemplate}>
              <Flex direction="column" gap="1">
                <Text weight="medium" size="1">
                  No-Yes
                </Text>
                <Text size="1" color="neutral">
                  2 options
                </Text>
              </Flex>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Flex>
      <DataTable.Root
        columns={columns}
        data={criterion.answerOptions.map((option, index) => ({ ...option, index }))}
        getRowId={(row: AnswerOptionRow) => row.index.toString()}
        variant="ghost"
      >
        {({ table }) => (
          <>
            <DataTable.Table>
              <DataTable.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <DataTable.HeaderGroup key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <DataTable.ColumnHeaderCell key={header.id} header={header} />
                    ))}
                  </DataTable.HeaderGroup>
                ))}
              </DataTable.Header>
              <DataTable.Body>
                {table.getRowModel().rows.map(row => (
                  <DataTable.Row key={row.id} row={row}>
                    {row.getVisibleCells().map(cell => (
                      <DataTable.Cell key={cell.id} cell={cell} className="align-middle" />
                    ))}
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable.Table>
          </>
        )}
      </DataTable.Root>
      <Flex gap="2" justify="start">
        <Button variant="ghost" color="neutral" onClick={addOption} size="1">
          <Icon label="add" color="neutral" icon={faPlus} size="2" />
          Add Option
        </Button>
      </Flex>
      {error && (
        <Text size="2" color="red">
          {error}
        </Text>
      )}
    </Flex>
  );
};

export const CriterionAnswerOptions = ({
  criterion,
  setCriterion,
  isEditingCriterion,
  error,
  questionError,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: Dispatch<SetStateAction<AuditRubricCriterion>>;
  isEditingCriterion: boolean;
  error?: string;
  questionError?: string;
}) => {
  return isEditingCriterion ? (
    <EditCriterionAnswerOptions
      criterion={criterion}
      setCriterion={setCriterion}
      error={error}
      questionError={questionError}
    />
  ) : (
    <ViewCriterionAnswerOptions criterion={criterion} />
  );
};
